import React, { useState } from 'react';
import { DatePicker, Select, TimeRangePickerProps } from 'antd';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { filtersKeys } from 'types/types';
import './MetricsLayout.scss';
import Metrics from './Charts/Metrics';
import { useSchemasAndTables } from 'hooks/jobs';

dayjs.extend(utc);
dayjs.extend(timezone);

interface SelectedRange {
  fromDate: number;
  toDate: number;
}

const MetricsLayout: React.FC = () => {
  const intl = useIntl();

  const defaultFromDate = dayjs().subtract(24, 'hour');
  const defaultToDate = dayjs();

  const [selectedRange, setSelectedRange] = useState<SelectedRange>({
    fromDate: defaultFromDate.unix(),
    toDate: defaultToDate.unix(),
  });
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [selectedSchema, setSelectedSchema] = useState<string | null>(null);
  const [formatedRange, setFormatedRange] = useState(
    `${
      filtersKeys.CREATED_AT_DATE_RANGE
    }: ${defaultFromDate.toISOString()} to ${defaultToDate.toISOString()}`
  );

  const [dateRangePicker, setDateRangePicker] = useState<
    [dayjs.Dayjs, dayjs.Dayjs]
  >([defaultFromDate, defaultToDate]);

  const [selectedMetric, setSelectedMetric] = useState('sparkDriverMemory'); // default state for selected component
  const { data: schemasAndTablesData, isLoading: isSchemasAndTablesLoading } =
    useSchemasAndTables();
  const rangePresets: TimeRangePickerProps['presets'] = [
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past15Minutes' }),
      value: [dayjs().subtract(15, 'minute'), dayjs()],
    },
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past1Hour' }),
      value: [dayjs().subtract(1, 'hour'), dayjs()],
    },
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past6Hours' }),
      value: [dayjs().subtract(6, 'hour'), dayjs()],
    },
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past12Hours' }),
      value: [dayjs().subtract(12, 'hour'), dayjs()],
    },
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past24Hours' }),
      value: [dayjs().subtract(24, 'hour'), dayjs()],
    },
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past7Days' }),
      value: [dayjs().subtract(7, 'day'), dayjs()],
    },
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past14Days' }),
      value: [dayjs().subtract(14, 'day'), dayjs()],
    },
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past30Days' }),
      value: [dayjs().subtract(30, 'day'), dayjs()],
    },
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past60Days' }),
      value: [dayjs().subtract(60, 'day'), dayjs()],
    },
    {
      label: intl.formatMessage({ id: 'metrics.dateRange.past90Days' }),
      value: [dayjs().subtract(90, 'day'), dayjs()],
    },
  ];

  const handleDateChange = (
    values: any,
    [startDate, endDate]: [string, string]
  ) => {
    const isFieldValueSet = !!(startDate && endDate);
    const today = dayjs().startOf('day');
    const endDateDayjs = dayjs(endDate).startOf('day');

    if (isFieldValueSet) {
      const fromDate = dayjs(values[0]);

      const toDate =
        endDateDayjs.isSame(today, 'day') || endDateDayjs.isAfter(today, 'day')
          ? dayjs()
          : dayjs(endDate).endOf('day');

      const fromDateUnix = fromDate.unix();
      const toDateUnix = toDate.unix();

      const fromDateModifiedISO = fromDate.toISOString();
      const toDateModifiedISO = toDate.toISOString();

      setSelectedRange({
        fromDate: fromDateUnix,
        toDate: toDateUnix,
      });

      setFormatedRange(
        `${filtersKeys.CREATED_AT_DATE_RANGE}: ${fromDateModifiedISO} to ${toDateModifiedISO}`
      );

      setDateRangePicker([fromDate, toDate]);
    }
  };

  const handleMetricChange = (value: string) => {
    setSelectedMetric(value);
  };

  return (
    <section className="metrics-layout">
      <div className="metrics-layout__filters">
        <Select
          defaultValue="sparkDriverMemory"
          onChange={handleMetricChange}
          className="metrics-layout__filters-select"
          options={[
            {
              value: 'sparkDriverMemory',
              label: intl.formatMessage({
                id: 'metrics.sparkDriverMemoryUsage',
              }),
            },
            {
              value: 'numberOfJobs',
              label: intl.formatMessage({ id: 'metrics.numberOfJobs' }),
            },
            {
              value: 'diskMetrics',
              label: intl.formatMessage({ id: 'metrics.diskMetrics' }),
            },
            {
              value: 'tableJobs',
              label: intl.formatMessage({ id: 'metrics.tableJobs' }),
            },
          ]}
        />

        <DatePicker.RangePicker
          className="metrics-layout__filters-range"
          presets={rangePresets}
          defaultPickerValue={dateRangePicker}
          value={dateRangePicker}
          onChange={handleDateChange}
        />
        {selectedMetric === 'tableJobs' && (
          <>
            <Select
              loading={isSchemasAndTablesLoading}
              placeholder="Select Schema"
              disabled={isSchemasAndTablesLoading}
              onChange={(value) => setSelectedSchema(value)}
              className="metrics-layout__filters-select">
              {schemasAndTablesData?.Schemas.map((schema) => (
                <Select.Option key={schema} value={schema}>
                  {schema}
                </Select.Option>
              ))}
            </Select>
            <Select
              loading={isSchemasAndTablesLoading}
              placeholder="Select Table"
              disabled={isSchemasAndTablesLoading}
              onChange={(value) => setSelectedTable(value)}
              className="metrics-layout__filters-select">
              {schemasAndTablesData?.Tables.map((table) => (
                <Select.Option key={table} value={table}>
                  {table}
                </Select.Option>
              ))}
            </Select>
          </>
        )}
      </div>
      <Metrics
        selectedRange={selectedRange}
        selectedMetric={selectedMetric}
        formatedRange={formatedRange}
        selectedTable={selectedTable}
        selectedSchema={selectedSchema}
      />
    </section>
  );
};

export default MetricsLayout;
